<template>
  <div class="mobile-nav" v-if="isMobileMenuOpen">
    <!-- 遮罩层 -->
    <div class="mobile-nav__overlay" @click="closeMobileMenu"></div>
    
    <!-- 侧边导航栏 -->
    <transition name="slide">
      <div class="mobile-nav__drawer" v-show="isMobileMenuOpen">
        <!-- 顶部用户信息 -->
        <div class="mobile-nav__header">
          <div class="mobile-nav__avatar">
            <img src="../assets/svg/user.svg" alt="">
          </div>
          <div class="mobile-nav__user-info">
            <div class="mobile-nav__username">{{ userInfo.realName }}</div>
            <div class="mobile-nav__role">{{ userInfo.roleName }}</div>
          </div>
          <div class="mobile-nav__close" @click="closeMobileMenu">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="#6A7077" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
        </div>

        <!-- 导航菜单 -->
        <nav class="mobile-nav__menu">
          <div 
            v-for="item in menuItems" 
            :key="item.path"
            class="mobile-nav__item"
            :class="{ 'mobile-nav__item--active': currentRoute === item.path }"
            @click="navigateTo(item.path)"
          >
            <div class="mobile-nav__item-icon" v-html="item.icon"></div>
            <span class="mobile-nav__item-text">{{ item.title }}</span>
            <svg v-if="currentRoute === item.path" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7.5 10L9.16667 11.6667L12.5 8.33333" stroke="#00D47E" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
        </nav>

        <!-- 底部操作 -->
        <div class="mobile-nav__footer">
          <div class="mobile-nav__action" @click="handleAction('workingHours')">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 5V10L13.5 13.5M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z" stroke="#6A7077" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span>休息时间</span>
          </div>
          <div class="mobile-nav__action" @click="handleAction('testRecord')">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 5H7C5.89543 5 5 5.89543 5 7V15C5 16.1046 5.89543 17 7 17H13C14.1046 17 15 16.1046 15 15V7C15 5.89543 14.1046 5 13 5H11M9 5C9 3.89543 9.89543 3 11 3H9C7.89543 3 7 3.89543 7 5H9ZM9 5V7H11V5M9 12L10.5 13.5L13.5 10.5" stroke="#6A7077" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span>测试记录</span>
          </div>
          <div class="mobile-nav__action" @click="handleAction('editPassword')">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5 10V8C5 5.23858 7.23858 3 10 3C12.7614 3 15 5.23858 15 8V10M10 13V15M7 17H13C14.1046 17 15 16.1046 15 15V12C15 10.8954 14.1046 10 13 10H7C5.89543 10 5 10.8954 5 12V15C5 16.1046 5.89543 17 7 17Z" stroke="#6A7077" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span>修改密码</span>
          </div>
          <div class="mobile-nav__action mobile-nav__action--logout" @click="handleLogout">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M13 13L17 10M17 10L13 7M17 10H7M7 3H5C3.89543 3 3 3.89543 3 5V15C3 16.1046 3.89543 17 5 17H7" stroke="#F56C6C" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span>退出登录</span>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useUserStore } from '@/stores/user'
import eventBus from '@/utils/eventBus'

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:isOpen', 'close'])

const route = useRoute()
const router = useRouter()
const userStore = useUserStore()

const isMobileMenuOpen = computed({
  get: () => props.isOpen,
  set: (val) => emit('update:isOpen', val)
})

const currentRoute = computed(() => route.path.substring(1))
const userInfo = computed(() => userStore.userInfo)

const menuItems = [
  {
    path: 'dashboard',
    title: '首页',
    icon: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M9 22V12H15V22" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
  },
  {
    path: 'course-sales',
    title: '销课中心',
    icon: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15M9 5C9 6.10457 9.89543 7 11 7H13C14.1046 7 15 6.10457 15 5M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5M12 12H15M12 16H15M9 12H9.01M9 16H9.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
  },
  {
    path: 'user-info',
    title: '用户信息',
    icon: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M17 20C17 18.3431 14.7614 17 12 17C9.23858 17 7 18.3431 7 20M21 17C21 15.7697 19.7604 14.7133 18 14.2373M3 17C3 15.7697 4.23964 14.7133 6 14.2373M18 10.2361C19.1046 9.9344 20 8.93498 20 7.75C20 6.50736 19.0128 5.47194 17.75 5.25M6 10.2361C4.89543 9.9344 4 8.93498 4 7.75C4 6.50736 4.98719 5.47194 6.25 5.25M15 7.75C15 9.40685 13.6569 10.75 12 10.75C10.3431 10.75 9 9.40685 9 7.75C9 6.09315 10.3431 4.75 12 4.75C13.6569 4.75 15 6.09315 15 7.75Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
  },
  {
    path: 'profile',
    title: '个人中心',
    icon: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
  }
]

const navigateTo = (path) => {
  router.push(`/${path}`)
  closeMobileMenu()
}

const closeMobileMenu = () => {
  emit('close')
}

const handleAction = (action) => {
  closeMobileMenu()
  if (action === 'workingHours') {
    router.push({ name: 'WorkingHours' })
  } else if (action === 'testRecord') {
    router.push({ name: 'TestRecord' })
  } else if (action === 'editPassword') {
    router.push('/profile')
    setTimeout(() => {
      eventBus.emit('open-dialog', 'editPassword')
    }, 300)
  }
}

const handleLogout = () => {
  closeMobileMenu()
  userStore.logout()
}
</script>

<style scoped lang="scss">
.mobile-nav {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: $z-index-modal;
  
  &__overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(4PX);
    animation: fadeIn 0.3s ease;
  }
  
  &__drawer {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    width: 280PX;
    max-width: 80vw;
    background: #fff;
    box-shadow: 2PX 0 8PX rgba(0, 0, 0, 0.15);
    display: flex;
    flex-direction: column;
    overflow-x: hidden;
    overflow-y: auto;
    @include smooth-scroll;
    @include safe-area-inset('left');
  }
  
  &__header {
    padding: $spacing-lg $spacing-md;
    border-bottom: 1PX solid #f0f0f0;
    display: flex;
    align-items: center;
    gap: $spacing-md;
    position: relative;
    background: linear-gradient(135deg, #f5f7fa 0%, #fff 100%);
    flex-shrink: 0;
    overflow: hidden;
  }
  
  &__avatar {
    width: 56PX;
    height: 56PX;
    border-radius: 50%;
    background: #f4f4f4;
    @include flex-center;
    
    img {
      width: 56PX;
      height: 56PX;
      border-radius: 50%;
    }
  }
  
  &__user-info {
    flex: 1;
  }
  
  &__username {
    font-size: $font-size-medium-mobile;
    font-weight: 600;
    color: #1A1A1A;
    margin-bottom: 4PX;
  }
  
  &__role {
    font-size: $font-size-xsmall-mobile;
    color: #6A7077;
  }
  
  &__close {
    @include touch-target(40PX);
    border-radius: 50%;
    background: #f4f4f4;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:active {
      background: #e0e0e0;
      transform: scale(0.95);
    }
  }
  
  &__menu {
    flex: 1;
    padding: $spacing-md 0;
    overflow-x: hidden;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 4PX;
  }
  
  &__item {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    width: calc(100% - #{$spacing-md});
    min-height: 48PX;
    padding: $spacing-md;
    margin: 0 $spacing-sm;
    border-radius: 8PX;
    cursor: pointer;
    transition: all 0.3s ease;
    flex-shrink: 0;
    white-space: nowrap;
    box-sizing: border-box;
    
    &:active {
      background: #f5f7fa;
      transform: scale(0.98);
    }
    
    &--active {
      background: rgba(0, 212, 126, 0.08);
      
      .mobile-nav__item-icon {
        color: $primary-color;
      }
      
      .mobile-nav__item-text {
        color: $primary-color;
        font-weight: 600;
      }
    }
  }
  
  &__item-icon {
    width: 24PX;
    height: 24PX;
    margin-right: $spacing-md;
    color: #6A7077;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    
    :deep(svg) {
      width: 24PX;
      height: 24PX;
    }
  }
  
  &__item-text {
    flex: 1;
    font-size: $font-size-medium-mobile;
    color: #1A1A1A;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  &__footer {
    padding: $spacing-md;
    border-top: 1PX solid #f0f0f0;
    background: #fafafa;
    display: flex;
    flex-direction: column;
    gap: 8PX;
    flex-shrink: 0;
    overflow: hidden;
  }
  
  &__action {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    min-height: 48PX;
    padding: $spacing-md;
    border-radius: 8PX;
    cursor: pointer;
    transition: all 0.3s ease;
    flex-shrink: 0;
    white-space: nowrap;
    box-sizing: border-box;
    
    &:active {
      background: #f0f0f0;
      transform: scale(0.98);
    }
    
    svg {
      width: 20PX;
      height: 20PX;
      margin-right: $spacing-md;
      flex-shrink: 0;
    }
    
    span {
      font-size: $font-size-small-mobile;
      color: #6A7077;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      flex: 1;
    }
    
    &--logout {
      background: #fff1f0;
      border: 1PX solid #ffccc7;
      margin-top: $spacing-sm;
      
      span {
        color: #F56C6C;
        font-weight: 500;
      }
      
      &:active {
        background: #ffe7e3;
      }
    }
  }
}

// 过渡动画
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(-100%);
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>
